// components/DashboardLayout.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCompass,
  FiCalendar,
  FiPlusCircle,
  FiUsers,
  FiUser,
  FiMenu,
  FiX,
  FiDownload,
  FiTrendingUp,
  FiMail,
  FiFileText,
  FiBell
} from 'react-icons/fi';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import '../styles/Dashboard.scss';

const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadUserProfile();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false);
      setUnreadCount(count || 0);
    };
    fetchUnread();

    const channel = supabase
      .channel('dashboard-notif-count')
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, fetchUnread)
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [user]);

  const loadUserProfile = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setUserProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    {
      id: 'explore',
      label: 'Explore',
      icon: FiCompass,
      path: '/explore',
      active: location.pathname === '/explore'
    },
    {
      id: 'invitations',
      label: 'Invitations',
      icon: FiCalendar,
      path: '/invitations',
      active: location.pathname === '/invitations'
    },
    {
      id: 'cards',
      label: 'Cards',
      icon: FiMail,
      path: '/greeting-cards',
      active: location.pathname === '/greeting-cards' || location.pathname.startsWith('/greeting-card')
    },
    {
      id: 'create',
      label: 'Create',
      icon: FiPlusCircle,
      special: true,
      path: '/create-event',
      active: location.pathname === '/invitations'

    },
    {
      id: 'upgrade',
      label: 'Upgrade',
      icon: FiTrendingUp,
      path: '/upgrade',
      active: location.pathname.startsWith('/upgrade'),
      highlight: true
    },
    {
      id: 'invoices',
      label: 'Invoices',
      icon: FiFileText,
      path: '/invoices',
      active: location.pathname === '/invoices'
    },
    {
      id: 'communities',
      label: 'Communities',
      icon: FiUsers,
      path: '/communities',
      active: location.pathname === '/communities'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: FiBell,
      path: '/notifications',
      active: location.pathname === '/notifications',
      badge: unreadCount > 0 ? unreadCount : null,
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: FiUser,
      path: userProfile?.username ? `/${userProfile.username}` : '/profile',
      active: location.pathname === `/${userProfile?.username}` || location.pathname === '/profile'
    },
  ];

  const handleNavClick = (item) => {
    if (item.action) {
      item.action();
    } else if (item.path) {
      navigate(item.path);
    }
    setMobileMenuOpen(false);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      {/* Background */}
      <div className="dashboard-background">
        <img src="/images/events/5.jpg" alt="Background" />
        <div className="dashboard-overlay"></div>
      </div>

      {/* Desktop Sidebar */}
      <aside className="desktop-sidebar">
        <div className="sidebar-content">
          <Link to="/explore" className="sidebar-logo">
            <h2>Invits</h2>
          </Link>

          <nav className="sidebar-nav">
            {navItems.map(item => (
              <button
                key={item.id}
                className={`nav-item ${item.active ? 'active' : ''} ${item.special ? 'special' : ''} ${item.highlight ? 'highlight' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                <span className="nav-icon-wrap">
                  <item.icon />
                  {item.badge && <span className="nav-badge">{item.badge > 9 ? '9+' : item.badge}</span>}
                </span>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          {/* User Profile Section */}
          {userProfile && (
            <div className="sidebar-profile">
              <div className="profile-avatar">
                {userProfile.profile_photo_cdn_url ? (
                  <img src={userProfile.profile_photo_cdn_url} alt={userProfile.first_name} />
                ) : (
                  <div className="avatar-placeholder">
                    <FiUser />
                  </div>
                )}
              </div>
              <div className="profile-info">
                <h4>{userProfile.first_name} {userProfile.last_name}</h4>
                {userProfile.username && <p>@{userProfile.username}</p>}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="mobile-header">
        <button 
          className="menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <FiX /> : <FiMenu />}
        </button>
        <h2 className="mobile-logo">Invits</h2>
        <div className="mobile-header-actions">
          <button className="mobile-notif-btn" onClick={() => navigate('/notifications')}>
            <FiBell size={20} />
            {unreadCount > 0 && (
              <span className="mobile-notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>
          <div className="mobile-profile-btn">
            {userProfile?.profile_photo_cdn_url ? (
              <img src={userProfile.profile_photo_cdn_url} alt="Profile" />
            ) : (
              <FiUser />
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            className="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <motion.div 
              className="mobile-menu"
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mobile-menu-header">
                <h3>Menu</h3>
                <button onClick={() => setMobileMenuOpen(false)}>
                  <FiX />
                </button>
              </div>
              <nav className="mobile-nav">
                {navItems.map(item => (
                  <button
                    key={item.id}
                    className={`nav-item ${item.active ? 'active' : ''} ${item.highlight ? 'highlight' : ''}`}
                    onClick={() => handleNavClick(item)}
                  >
                    <item.icon />
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="dashboard-main">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        {navItems.filter(item => item.id !== 'upgrade' && item.id !== 'cards').map(item => (
          <button
            key={item.id}
            className={`nav-item ${item.active ? 'active' : ''} ${item.special ? 'special' : ''}`}
            onClick={() => handleNavClick(item)}
          >
            <item.icon />
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Download App Modal */}
      <AnimatePresence>
        {showDownloadModal && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDownloadModal(false)}
          >
            <motion.div 
              className="download-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                className="modal-close"
                onClick={() => setShowDownloadModal(false)}
              >
                <FiX />
              </button>
              
              <div className="modal-content">
                <div className="download-icon">
                  <FiDownload />
                </div>
                <h3>Download the App to Create</h3>
                <p>Create amazing invitations with our mobile app. Download now to get started!</p>
                
                <div className="download-buttons">
                  <a 
                    href="https://apps.apple.com/app/invits" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <span>Download on App Store</span>
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.invits" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-secondary"
                  >
                    <span>Get it on Google Play</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DashboardLayout;